/**
 * Taxonomy cố định của kho tài liệu (kế hoạch mục 3.2).
 * Dùng hằng số thay cho collection để MVP đơn giản + cache dễ.
 * Admin quản lý taxonomy động sẽ bổ sung ở giai đoạn sau.
 */

export interface Option {
  value: string;
  label: string;
}

export const GRADES = [1, 2, 3, 4, 5] as const;
export type Grade = (typeof GRADES)[number];

export const SUBJECTS: Option[] = [
  { value: "toan", label: "Toán" },
  { value: "tieng-viet", label: "Tiếng Việt" },
  { value: "tieng-anh", label: "Tiếng Anh" },
  { value: "tnxh", label: "Tự nhiên và Xã hội" },
  { value: "khoa-hoc", label: "Khoa học" },
  { value: "lich-su-dia-li", label: "Lịch sử và Địa lí" },
  { value: "dao-duc", label: "Đạo đức" },
  { value: "tin-hoc", label: "Tin học" },
  { value: "cong-nghe", label: "Công nghệ" },
  { value: "am-nhac", label: "Âm nhạc" },
  { value: "mi-thuat", label: "Mĩ thuật" },
  { value: "hdtn", label: "Hoạt động trải nghiệm" },
  { value: "the-chat", label: "Giáo dục thể chất" },
];

export const DOC_TYPES: Option[] = [
  { value: "giao-an", label: "Giáo án" },
  { value: "sgk", label: "Sách giáo khoa" },
  { value: "vo-bai-tap", label: "Vở bài tập" },
  { value: "bai-giang", label: "Bài giảng điện tử" },
  { value: "phieu-on-tap", label: "Phiếu ôn tập" },
  { value: "phieu-bai-tap", label: "Phiếu bài tập" },
  { value: "de-kiem-tra", label: "Đề kiểm tra" },
  { value: "de-on-tap", label: "Đề ôn tập" },
  { value: "dap-an", label: "Đáp án" },
  { value: "tai-lieu-ph", label: "Tài liệu phụ huynh" },
  { value: "hoc-lieu-truc-quan", label: "Học liệu trực quan" },
  { value: "mau-tro-choi", label: "Mẫu trò chơi" },
];

export const BOOK_SERIES: Option[] = [
  { value: "ket-noi-tri-thuc", label: "Kết nối tri thức với cuộc sống" },
  { value: "chan-troi-sang-tao", label: "Chân trời sáng tạo" },
  { value: "canh-dieu", label: "Cánh Diều" },
  { value: "khac", label: "Khác" },
];

export const TERMS: Option[] = [
  { value: "hk1", label: "Học kì 1" },
  { value: "hk2", label: "Học kì 2" },
  { value: "ca-nam", label: "Cả năm" },
];

export const WEEKS = Array.from({ length: 35 }, (_, i) => i + 1);

export const SORT_OPTIONS: Option[] = [
  { value: "newest", label: "Mới nhất" },
  { value: "popular", label: "Phổ biến" },
  { value: "downloads", label: "Tải nhiều" },
  { value: "rating", label: "Đánh giá cao" },
];

/** Nhóm hiển thị ở trang chủ "Tài liệu dành cho thầy cô" (mỗi nhóm gồm nhiều loại). */
export const SHOWCASE_GROUPS: { key: string; label: string; types: string[] }[] = [
  { key: "giao-an", label: "Giáo án", types: ["giao-an", "bai-giang"] },
  { key: "sgk-vbt", label: "Sách giáo khoa & Vở bài tập", types: ["sgk", "vo-bai-tap"] },
  { key: "phieu-on-tap", label: "Phiếu ôn tập", types: ["phieu-on-tap", "phieu-bai-tap"] },
  { key: "de-kiem-tra", label: "Đề kiểm tra", types: ["de-kiem-tra", "de-on-tap"] },
];

export const DOC_TYPE_VALUES = DOC_TYPES.map((o) => o.value);
export const SUBJECT_VALUES = SUBJECTS.map((o) => o.value);
export const BOOK_SERIES_VALUES = BOOK_SERIES.map((o) => o.value);
export const TERM_VALUES = TERMS.map((o) => o.value);

/** Tra label từ value (dùng khi render). */
export function labelOf(options: Option[], value?: string | null): string {
  if (!value) return "";
  return options.find((o) => o.value === value)?.label ?? value;
}

/** Định dạng file được phép upload → nhãn hiển thị. */
export const ALLOWED_MIME: Record<string, string> = {
  "application/pdf": "PDF",
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/webp": "WEBP",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/vnd.ms-powerpoint": "PPT",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/zip": "ZIP",
  "text/plain": "TXT",
};

export const MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB (gói miễn phí)

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
